// Versão modificada do ItemListRelatorioAM com as novas funcionalidades

import { TIPO_INSPECAO } from "@/constants/TIPO_INSPECAO";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePermissao } from "@/hooks/usePermissao";
import { useRelatorioAM } from "@/hooks/useRelatorioAM";
import { IRelatorioAM } from "@/interfaces/IRelatorioAM";
import { ImpresssaoRelatorioAM } from "@/pages/relatorioAM/_components/ImpresssaoRelatorioAM";
import { ModalCancelarRAM } from "@/pages/relatorioAM/_components/ModalCancelarRAM";
import { convertBase64ToFile } from "@/utils/convertBase64ToFile";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MinusOutlined,
  PrinterOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { App, Button, List, Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

export function ItemListRelatorioAM({ item }: { item: IRelatorioAM }) {
  const { isOnline } = useNetworkStatus();
  const relatorioRef = useRef<HTMLDivElement>(null);
  const {
    submitRelatorioAMApi,
    updateRelatorioAM,
    removeRelatorioAM,
    updateRelatorioAMApi,
    validateFinalizadoRelatorio,
    canCancelRelatorio,
    canViewAndPrintRelatorio,
    canEditRelatorio,
  } = useRelatorioAM();
  const { hasEnabledPermissao } = usePermissao();
  const { notification, message, modal } = App.useApp();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModalCancelar, setIsOpenModalCancelar] = useState(false);

  const handleSync = async () => {
    try {
      message.loading({
        content: "Sincronizando Relatório de Auditoria e Monitoria...",
        key: "loading",
        duration: 0,
      });
      setIsLoading(true);
      const formData = new FormData();

      const request: IRelatorioAM = {
        ...item,
        itens: item.itens.map((item) => ({
          ...item,
          anexos: item.anexos.map((anexo) => ({
            nomeArquivo: anexo.nomeArquivo,
            nomeUpload: anexo.nomeUpload,
          })),
        })),
      };

      // Converter anexos base64 para File e adicionar ao FormData
      const anexosRequest: File[] = [];

      item.itens.forEach((itemRelatorio) => {
        if (itemRelatorio.anexos.length > 0) {
          itemRelatorio.anexos.forEach((anexo) => {
            if (anexo.arquivoBase64) {
              const arquivo = convertBase64ToFile(
                anexo.arquivoBase64,
                anexo.nomeArquivo
              );

              if (arquivo) {
                anexosRequest.push(arquivo);
              }
            }
          });
        }
      });

      // Adicionar arquivos convertidos ao FormData
      anexosRequest.forEach((arquivo) => {
        formData.append("anexos", arquivo, arquivo.name);
      });

      if (item.id) {
        const requestUpdate = {
          ...request,
          itens: request.itens.filter((item) => item.alternativa),
        };

        formData.append(
          "ramRequest",
          new Blob([JSON.stringify(requestUpdate)], {
            type: "application/json",
          })
        );

        await updateRelatorioAMApi(formData, item.id);
        updateRelatorioAM({
          ...item,
          isUpdated: true,
        });
      } else {
        formData.append(
          "ramRequest",
          new Blob([JSON.stringify(request)], {
            type: "application/json",
          })
        );
        const result = await submitRelatorioAMApi(formData);
        updateRelatorioAM({
          ...item,
          id: result.id,
          isUpdated: true,
        });
      }

      setIsLoading(false);
      message.destroy("loading");

      notification.success({
        message: "Relatório de Auditoria e Monitoria sincronizado com sucesso!",
      });
    } catch (error) {
      console.error(
        "Erro ao sincronizar o Relatório de Auditoria e Monitoria:",
        error
      );
      message.destroy("loading");
      setIsLoading(false);
      const messageError = formatMessageError(error);
      notification.error({
        message:
          "Não foi possível sincronizar o Relatório de Auditoria e Monitoria",
        description: messageError,
      });
    }
  };

  const handleFinalize = async () => {
    try {
      // validacao se a fiscalizacao tem data, hora inicial e hora final
      const fiscalizacoesInvalidas = (item.fiscalizacoes || []).filter(
        (fiscalizacao) =>
          !fiscalizacao.data ||
          !fiscalizacao.horaInicial ||
          !fiscalizacao.horaFinal
      );
      if (fiscalizacoesInvalidas.length > 0) {
        notification.error({
          message: "Não foi possivel finalizar o relatório",
          description:
            "Existem fiscalizações sem data, hora inicial ou hora final. Por favor, preencha todas as fiscalizações.",
        });
        return;
      }

      // Validações para finalizar
      if (!item.itens || item.itens.length === 0) {
        notification.error({
          message: "Não é possível finalizar o relatório",
          description: "Nenhuma pergunta respondida.",
        });
        return;
      }

      // Verificar se há perguntas sem alternativa selecionada
      const perguntasIncompletas = item.itens.filter(
        (itemRelatorio) =>
          !itemRelatorio.alternativa || itemRelatorio.alternativa.id === 0
      );

      if (perguntasIncompletas.length > 0) {
        notification.error({
          message: "Não é possível finalizar o relatório",
          description:
            "Existem perguntas sem alternativa selecionada. Por favor, edite o relatório e responda todas as perguntas.",
        });
        return;
      }

      // Verificar se há pelo menos uma assinatura
      if (!item.assinaturas || item.assinaturas.length === 0) {
        notification.error({
          message: "Não é possível finalizar o relatório",
          description:
            "É necessário adicionar pelo menos uma assinatura. Por favor, edite o relatório.",
        });
        return;
      }

      // Verificar campos obrigatórios
      if (!item.conclusao?.trim()) {
        notification.error({
          message: "Não é possível finalizar o relatório",
          description:
            "O campo conclusão é obrigatório. Por favor, edite o relatório.",
        });
        return;
      }

      modal.confirm({
        title: "Finalizar Relatório",
        content:
          "Tem certeza que deseja finalizar este relatório? Após finalizado, não será possível editar.",
        okText: "Finalizar",
        okType: "primary",
        cancelText: "Cancelar",
        onOk: async () => {
          try {
            message.loading({
              content: "Finalizando Relatório de Auditoria e Monitoria...",
              key: "finalizing",
              duration: 0,
            });
            setIsLoading(true);

            const formData = new FormData();

            const request: IRelatorioAM = {
              ...item,
              itens: item.itens.map((item) => ({
                ...item,
                anexos: item.anexos.map((anexo) => ({
                  nomeArquivo: anexo.nomeArquivo,
                  nomeUpload: anexo.nomeUpload,
                })),
              })),
            };

            // Processar anexos se necessário
            const anexosRequest: File[] = [];

            item.itens.forEach((itemRelatorio) => {
              if (itemRelatorio.anexos.length > 0) {
                itemRelatorio.anexos.forEach((anexo) => {
                  if (anexo.arquivoBase64) {
                    const arquivo = convertBase64ToFile(
                      anexo.arquivoBase64,
                      anexo.nomeArquivo
                    );

                    if (arquivo) {
                      anexosRequest.push(arquivo);
                    }
                  }
                });
              }
            });

            anexosRequest.forEach((arquivo) => {
              formData.append("anexos", arquivo, arquivo.name);
            });

            formData.append(
              "ramRequest",
              new Blob([JSON.stringify(request)], {
                type: "application/json",
              })
            );

            let result;

            if (item.id) {
              // Se já tem ID, usar update
              result = await updateRelatorioAMApi(formData, item.id);
            } else {
              // Se não tem ID, usar create
              result = await submitRelatorioAMApi(formData);
            }

            setIsLoading(false);
            message.destroy("finalizing");

            updateRelatorioAM({
              ...item,
              ...result,
            });

            notification.success({
              message: "Relatório finalizado com sucesso!",
              description:
                "O relatório foi finalizado e sincronizado com o servidor.",
            });
          } catch (error) {
            console.error("Erro ao finalizar:", error);
            message.destroy("finalizing");
            setIsLoading(false);
            const messageError = formatMessageError(error);
            notification.error({
              message: "Não foi possível finalizar o relatório",
              description: messageError,
            });
          }
        },
      });
    } catch (error) {
      console.error("Erro na validação:", error);
      notification.error({
        message: "Erro na validação",
        description: "Não foi possível validar o relatório para finalização.",
      });
    }
  };

  const handleRemove = () => {
    modal.confirm({
      title: "Remover Relatório de Auditoria e Monitoria",
      content:
        "Tem certeza que deseja remover este Relatório de Auditoria e Monitoria?",
      okText: "Remover",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        removeRelatorioAM(item.offlineId!);
        notification.success({
          message: "Relatório de Auditoria e Monitoria removido com sucesso!",
        });
      },
    });
  };

  const redirectToEdit = () => {
    navigate(`/RAM/lista/editar/${item.offlineId}`);
  };

  const redirectToView = () => {
    navigate(`/RAM/visualizar/${item.id}`);
  };

  const handleRemoveLocal = () => {
    removeRelatorioAM(item.offlineId!);
  };

  const handlePrint = useReactToPrint({
    contentRef: relatorioRef,
    documentTitle: "relatorio_am",
    suppressErrors: true,
    preserveAfterPrint: true,
  });

  const handleOpenModalCancelar = () => {
    setIsOpenModalCancelar(true);
  };

  const enabledCancelAction = canCancelRelatorio(
    item.formulario.tipoFormulario,
    item.estabelecimento.id
  );

  const enabledViewAndPrintAction = canViewAndPrintRelatorio(
    item.formulario.classificacaoFormulario,
    item.estabelecimento.id
  );

  const enabledEditAction = canEditRelatorio(item.estabelecimento.id);

  // Determinar o status do relatório
  const isRascunho = validateFinalizadoRelatorio(item) === false;
  const statusText = item.id
    ? `RAM ${item.id}`
    : isRascunho
      ? "RAM (Rascunho)"
      : "RAM (Pendente)";

  return (
    <>
      <List.Item
        extra={
          <Space>
            {isRascunho && !item.isUpdated && enabledEditAction && (
              <Button
                type="primary"
                size="small"
                ghost
                icon={<UploadOutlined />}
                onClick={handleSync}
                disabled={!isOnline || isLoading}
                loading={isLoading}
              >
                Sincronizar
              </Button>
            )}

            {!isRascunho && !item.isUpdated && enabledEditAction && (
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={handleFinalize}
                disabled={!isOnline || isLoading}
                loading={isLoading}
              >
                Finalizar
              </Button>
            )}

            {enabledViewAndPrintAction && (
              <Tooltip title="Imprimir" destroyOnHidden>
                <Button
                  size="small"
                  icon={<PrinterOutlined />}
                  onClick={handlePrint}
                />
              </Tooltip>
            )}

            {enabledEditAction && (
              <Tooltip title="Editar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={redirectToEdit}
                  disabled={
                    !!item.id &&
                    (item.situacao !== "PENDENTE" ||
                      !hasEnabledPermissao("RAM_EDITAR"))
                  }
                />
              </Tooltip>
            )}

            {item.id && enabledViewAndPrintAction && (
              <Tooltip title="Visualizar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={redirectToView}
                  disabled={!isOnline || !hasEnabledPermissao("RAM_VISUALIZAR")}
                />
              </Tooltip>
            )}

            {!item.id && (
              <Tooltip title="Remover" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemove}
                />
              </Tooltip>
            )}

            {item.id && enabledCancelAction && (
              <Tooltip title="Cancelar" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleOpenModalCancelar}
                  disabled={!isOnline || !hasEnabledPermissao("RAM_CANCELAR")}
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Remover (local)" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<MinusOutlined />}
                  onClick={handleRemoveLocal}
                  disabled={!isOnline}
                />
              </Tooltip>
            )}
          </Space>
        }
      >
        <List.Item.Meta
          title={
            <Space>
              <Typography.Text
                strong
                style={{
                  color: isOpenModalCancelar ? "#d7263d" : undefined,
                  opacity: item.id ? 1 : 0.6,
                }}
              >
                {statusText}
              </Typography.Text>
              {item.situacao === "PENDENTE" && (
                <Tag color="orange">Pendente</Tag>
              )}

              {item.situacao !== "PENDENTE" && (
                <Tag color="green">Finalizado</Tag>
              )}
            </Space>
          }
          description={
            <Typography.Text
              style={{
                color: isOpenModalCancelar ? "#d7263d" : "#8c8c8c",
                fontSize: 12,
              }}
            >
              Estabelecimento: {item.estabelecimento.numeroInspecao}-
              {TIPO_INSPECAO[item.estabelecimento.tipoInspecao]}-
              {item.estabelecimento.nome} <br />
              Data Preenchimento:{" "}
              {item.dataMonitoria
                ? dayjs(item.dataMonitoria).format("DD/MM/YYYY")
                : "-"}
            </Typography.Text>
          }
        />
      </List.Item>

      <div style={{ display: "none" }}>
        <ImpresssaoRelatorioAM marcadagua="" ref={relatorioRef} item={item} />
      </div>

      <ModalCancelarRAM
        isOpen={isOpenModalCancelar}
        onClose={() => setIsOpenModalCancelar(false)}
        ramId={item.id!}
        reloadItems={() => {}}
      />
    </>
  );
}
